import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  RequiredValidator,
  Validators,
} from '@angular/forms';
import { NotifierService } from 'angular-notifier';
import { BoobleModalService } from 'booble-modal';
import { map, Observable, Subject, takeUntil } from 'rxjs';
import { Moedas } from 'src/app/domain/constants/moeda.constant';
import { TreasuryInfoDto } from 'src/app/domain/models/dto/treasury-info.dto';
import { BankAccountService } from 'src/app/shared/services/bank-account.service';
import { OrdersService } from 'src/app/shared/services/orders.service';

@Component({
  selector: 'app-treasury-info',
  templateUrl: './treasury-info.component.html',
  styleUrls: ['./treasury-info.component.scss'],
})
export class TreasuryInfoComponent implements OnInit, OnDestroy {
  boobleModalData: any;
  currency: any;
  moedas = Moedas;
  showReadMore = true;
  iof: number;
  ir: number;
  commercial: number;

  formTreasury!: FormGroup;
  formTreasuryInbound!: FormGroup;

  private readonly notifier: NotifierService;
  public loading: boolean;
  private destroy$ = new Subject<void>();

  deme$: Observable<string[]>;

  constructor(
    private modalService: BoobleModalService,
    formBuilder: FormBuilder,
    private readonly ordersService: OrdersService,
    private readonly bankService: BankAccountService,
    notifierService: NotifierService
  ) {
    this.notifier = notifierService;

    this.formTreasury = formBuilder.group({
      BankCurrencyTreasury: ['', Validators.required],
      LevelingRateTreasury: [
        null,
        [Validators.required, Validators.pattern('.*\\S.*')],
      ],
      SettlementFlowTreasury: ['', Validators.required],
      treasuryObservation: [''],
    });
    this.formTreasuryInbound = formBuilder.group({
      LevelingRateTreasury: [
        null,
        [Validators.required, Validators.pattern('.*\\S.*')],
      ],
      treasuryObservation: [''],
    });
  }

  get treasury(): TreasuryInfoDto {
    const treasury = this.formTreasury.value;
    return treasury;
  }

  get treasuryInbound(): TreasuryInfoDto {
    const treasuryInbound = this.formTreasuryInbound.value;
    return treasuryInbound;
  }

  ngOnInit() {
    if (typeof this.boobleModalData === 'number') {
      this.ordersService
        .getOrderById(this.boobleModalData, "true")
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (result) => {
            this.boobleModalData = result.data;
            this.initializeComponent();
          },
          error: (err) => {},
        });
    } else {
      this.initializeComponent();
    }
  }

  initializeComponent() {
    this.currency = this.moedas.find(
      (moeda) => moeda.id === this.boobleModalData.currency.id
    );

    this.deme$ = this.bankService
      .getDemeBanks(this.currency.value)
      .pipe(
        map((v) =>
          v.data
            .filter((x) =>
              x.remmitanceTypes.includes(this.boobleModalData.remmitanceType)
            )
            .map((x) => x.bankName)
        )
      );

    if (this.boobleModalData.remmitanceType === 'INBOUND') {
      this.boobleModalData?.levelingRateTreasury
        ? this.formTreasuryInbound.controls['LevelingRateTreasury'].patchValue(
            this.boobleModalData?.levelingRateTreasury
          )
        : null;
      this.boobleModalData?.treasuryObservation
        ? this.formTreasuryInbound.controls['treasuryObservation'].patchValue(
            this.boobleModalData?.treasuryObservation
          )
        : null;
    } else if (this.boobleModalData.remmitanceType === 'OUTBOUND') {
      this.boobleModalData?.bankCurrencyTreasury
        ? this.formTreasury.controls['BankCurrencyTreasury'].patchValue(
            this.boobleModalData?.bankCurrencyTreasury
          )
        : null;
      this.boobleModalData?.levelingRateTreasury
        ? this.formTreasury.controls['LevelingRateTreasury'].patchValue(
            this.boobleModalData?.levelingRateTreasury
          )
        : null;

      this.boobleModalData?.settlementFlowTreasury
        ? this.formTreasury.controls['SettlementFlowTreasury'].patchValue(
            this.boobleModalData?.settlementFlowTreasury
          )
        : null;
      this.boobleModalData?.treasuryObservation
        ? this.formTreasury.controls['treasuryObservation'].patchValue(
            this.boobleModalData?.treasuryObservation
          )
        : null;
    }

    if (this.boobleModalData.quotations?.length > 0) {
      if (this.boobleModalData.remmitanceType === 'OUTBOUND') {
        this.ir = this.boobleModalData.quotations[0].irOutbound;
        this.iof = this.boobleModalData.quotations[0].iofOutbound;
        this.commercial = Number(
          (
            this.boobleModalData.quotations[0].rate +
            this.boobleModalData.quotations[0].rate *
              this.boobleModalData.quotations[0].spread
          ).toPrecision(5)
        );
      } else if (this.boobleModalData.remmitanceType === 'INBOUND') {
        this.ir = this.boobleModalData.quotations[0].irInbound;
        this.iof = this.boobleModalData.quotations[0].iofInboud;
        this.commercial = Number(
          (
            this.boobleModalData.quotations[0].rate -
            this.boobleModalData.quotations[0].rate *
              this.boobleModalData.quotations[0].spread
          ).toPrecision(5)
        );
      }
    }
  }

  closeModal() {
    this.modalService.dismiss(null);
  }

  preventNegative(event: KeyboardEvent) {
    if (event.key === '-') {
      event.preventDefault();
    }
  }

  getModalityText(modalityCode: number) {
    switch (modalityCode) {
      case 1:
        return 'À Vista';
      case 2:
        return 'Antecipado';
      case 3:
        return 'À Prazo';
      default:
        return '-';
    }
  }

  ensurePositiveValue(formControlName: string, form: FormGroup) {
    let value = form.controls[formControlName].value;

    if (typeof value === 'string') {
      value = value.replace(/[R$\s.]/g, '').replace(',', '.');
      const numericValue = parseFloat(value);

      if (!isNaN(numericValue) && numericValue < 0) {
        const positiveValue = Math.abs(numericValue).toFixed(8);
        form.controls[formControlName].setValue(positiveValue);
      }
    } else if (typeof value === 'number') {
      if (value < 0) {
        const positiveValue = Math.abs(value).toFixed(8);
        form.controls[formControlName].setValue(positiveValue);
      }
    }
  }

  increasePercentage(value: number, percentage: number) {
    return value + value * percentage;
  }

  getIr(rate: number, spread: number, taxIr: number, ptax: number) {
    const residualIr =
      ((this.increasePercentage(rate, spread) < ptax
        ? ptax
        : this.increasePercentage(rate, spread)) *
        this.boobleModalData.amount) /
      (1 - taxIr);
    return residualIr * taxIr;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
  sendTreasury() {
    this.loading = true;

    let form =
      this.boobleModalData?.remmitanceType == 'INBOUND'
        ? this.treasuryInbound
        : this.treasury;

    this.ordersService.treasuryInfo(this.boobleModalData?.id, form).subscribe({
      next: (treasuryResult) => {
        this.loading = false;
        this.closeModal();
        this.notifier.notify(
          'success',
          'Informações da tesouraria adicionadas com sucesso.'
        );
        this.ordersService.updateOrderView();
      },
      error: (err) => {
        this.loading = false;
      },
    });
  }
}
